import {
  ApiReturnCampaign,
  CalendarTimeRangeConfig,
  FixedTimeRangeConfig,
  IntervalJSON,
  RelativeTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import { PermissionDecorator } from '@highimpact/react-decorators';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import CalendarTimeRangeForm from './CalendarTimeRangeForm';
import FixedTimeRangeForm from './FixedTimeRangeForm';
import RelativeTimeRangeForm from './RelativeTimeRangeForm';

type TimeRangeUnionProps = {
  onChange: (range: IntervalJSON[]) => void;
  campaigns: ApiReturnCampaign[];
  noDivision?: boolean;
  noCampaigns?: boolean;
  noCalendar?: boolean;
  noRelative?: boolean;
  noRelativeFuture?: boolean;
  defaultIntervals?: IntervalJSON[];
  matchControlPanelStyle?: boolean;
};

type Option = 'campaign' | 'calendar' | 'relative';

function isCalendarTimeRange(i: IntervalJSON): i is CalendarTimeRangeConfig {
  return (i as CalendarTimeRangeConfig).type === 'calendar_time_range';
}

function isRelativeTimeRange(i: IntervalJSON): i is RelativeTimeRangeConfig {
  return (i as RelativeTimeRangeConfig).type === 'relative_time_range';
}

function isFixedTimeRange(i: IntervalJSON): i is FixedTimeRangeConfig {
  return (i as FixedTimeRangeConfig).type === 'fixed_time_range';
}

function isCalendarOrRelativeNow(
  i: IntervalJSON
): i is RelativeTimeRangeConfig | CalendarTimeRangeConfig {
  if (isCalendarTimeRange(i)) {
    return i.relative_to === 'now';
  }
  if (isRelativeTimeRange(i)) {
    return i.relative_to === 'now';
  }
  return false;
}

export function isFixedTimeRangeNamedAfterACampaign(
  i: IntervalJSON,
  campaigns: ApiReturnCampaign[]
): i is FixedTimeRangeConfig {
  return (
    isFixedTimeRange(i) &&
    Boolean(i.campaign_ids) &&
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    i.campaign_ids!.some((id) => campaigns.some((c) => c.id === id))
  );
}

function isFixedTimeRangeAndNotACampaign(
  i: IntervalJSON,
  campaigns: ApiReturnCampaign[]
): i is FixedTimeRangeConfig {
  return (
    isFixedTimeRange(i) &&
    !(
      Boolean(i.campaign_ids) &&
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      i.campaign_ids!.some((id) => campaigns.some((c) => c.id === id))
    )
  );
}

function isCalendarTimeRangeOrFixedRangeFromCalendar(
  i: IntervalJSON,
  campaigns: ApiReturnCampaign[]
): i is FixedTimeRangeConfig | CalendarTimeRangeConfig {
  return (
    isFixedTimeRangeAndNotACampaign(i, campaigns) || isCalendarTimeRange(i)
  );
}

/**
 * Validate intervals into one of: calendar | relative | fixed | null (invalid/mixed)
 */
export function findTimeRangeType(
  intervals: IntervalJSON[] | undefined,
  campaigns: ApiReturnCampaign[]
) {
  if (!intervals || intervals.length === 0) return null;

  const allFixed = intervals.every((i) =>
    isFixedTimeRangeNamedAfterACampaign(i, campaigns)
  );
  if (allFixed)
    return {
      kind: 'fixed' as const,
      intervals: intervals as FixedTimeRangeConfig[],
    };

  const allCalendarOrRelativeNow = intervals.every(isCalendarOrRelativeNow);
  if (allCalendarOrRelativeNow)
    return {
      kind: 'relative' as const,
      intervals,
    };

  const allCalendar = intervals.every((i) =>
    isCalendarTimeRangeOrFixedRangeFromCalendar(i, campaigns)
  );
  if (allCalendar)
    return {
      kind: 'calendar' as const,
      intervals: intervals as (
        | FixedTimeRangeConfig
        | CalendarTimeRangeConfig
      )[],
    };

  return null; // mixed or unsupported combination
}

function TimeRangeUnionForm({
  onChange,
  campaigns,
  noDivision = false,
  noCampaigns = false,
  noCalendar = false,
  noRelative = false,
  noRelativeFuture = true,
  defaultIntervals,
  matchControlPanelStyle = false,
}: TimeRangeUnionProps) {
  const validatedDefault = useMemo(() => {
    return findTimeRangeType(defaultIntervals, campaigns);
  }, [defaultIntervals, campaigns]);

  // Decide initial option
  const resolvedInitialOption: Option = (() => {
    const allowed: Option[] = [];
    if (!noCampaigns && campaigns.length > 0) allowed.push('campaign');
    if (!noCalendar) allowed.push('calendar');
    if (!noRelative) allowed.push('relative');

    if (allowed.length === 0) {
      // nothing allowed, fallback to calendar
      return 'calendar';
    }
    if (validatedDefault) {
      if (validatedDefault.kind === 'fixed' && allowed.includes('campaign'))
        return 'campaign';
      if (validatedDefault.kind === 'relative' && allowed.includes('relative'))
        return 'relative';
      if (validatedDefault.kind === 'calendar' && allowed.includes('calendar'))
        return 'calendar';
    }
    // Prefer calendar if available, then relative, then campaign
    if (allowed.includes('calendar')) return 'calendar';
    if (allowed.includes('relative')) return 'relative';
    return 'campaign';
  })();

  const [selectedOption, setSelectedOption] = useState<Option>(
    resolvedInitialOption
  );

  // Prepare defaults for each form only if they match and are allowed
  const fixedDefaultIntervals =
    validatedDefault && validatedDefault.kind === 'fixed' && !noCampaigns
      ? (validatedDefault.intervals as FixedTimeRangeConfig[])
      : undefined;

  const calendarDefaultIntervals =
    validatedDefault && validatedDefault.kind === 'calendar' && !noCalendar
      ? (validatedDefault.intervals as (
          | FixedTimeRangeConfig
          | CalendarTimeRangeConfig
        )[])
      : undefined;

  const relativeDefaultIntervals =
    validatedDefault && validatedDefault.kind === 'relative' && !noRelative
      ? (validatedDefault.intervals as (
          | CalendarTimeRangeConfig
          | RelativeTimeRangeConfig
        )[])
      : undefined;

  const renderForm = () => {
    if (selectedOption === 'campaign') {
      return (
        <FixedTimeRangeForm
          onChange={onChange}
          campaigns={campaigns}
          noDivision={noDivision}
          {...(fixedDefaultIntervals
            ? { defaultIntervals: fixedDefaultIntervals }
            : {})}
        />
      );
    }
    if (selectedOption === 'relative') {
      return (
        <RelativeTimeRangeForm
          onChange={onChange}
          noDivision={noDivision}
          noRelativeFuture={noRelativeFuture}
          {...(relativeDefaultIntervals
            ? { defaultIntervals: relativeDefaultIntervals }
            : {})}
        />
      );
    }
    return (
      <CalendarTimeRangeForm
        onChange={onChange}
        noDivision={noDivision}
        {...(calendarDefaultIntervals
          ? { defaultIntervals: calendarDefaultIntervals }
          : {})}
      />
    );
  };

  return (
    <Box
      display="block"
      p={matchControlPanelStyle ? 2 : 0}
      width={matchControlPanelStyle ? 300 : undefined}
    >
      {matchControlPanelStyle ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: '0.9rem', fontWeight: 700 }}
            mb={1}
          >
            Plages temporelles
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              bgcolor: 'background.paper',
              px: 1.5,
              py: 0.75,
              '& > .MuiFormControl-root': {
                mt: 0,
                mb: 1,
              },
              '& .MuiInputBase-root': {
                minHeight: 36,
              },
              '& .MuiOutlinedInput-input': {
                py: 0.75,
              },
              '& > .MuiFormControl-root:last-of-type': {
                mb: 0,
              },
            }}
          >
            {!(
              (noCampaigns ? 0 : 1) +
                (noCalendar ? 0 : 1) +
                (noRelative ? 0 : 1) <=
              1
            ) ? (
              <FormControl
                className="nav-form-group"
                sx={{ width: '100%', mb: 1 }}
              >
                <RadioGroup
                  row
                  name="dynamicRender"
                  value={selectedOption}
                  onChange={(e) =>
                    setSelectedOption(e.currentTarget.value as Option)
                  }
                  sx={{ gap: 0.5 }}
                >
                  {!noCalendar && (
                    <FormControlLabel
                      value="calendar"
                      control={<Radio size="small" />}
                      label="calendaires"
                      sx={{ mr: 1.25 }}
                    />
                  )}
                  {!noRelative && (
                    <FormControlLabel
                      value="relative"
                      control={<Radio size="small" />}
                      label="relatifs"
                      sx={{ mr: 1.25 }}
                    />
                  )}
                  <PermissionDecorator item="campaigns_tab">
                    {!noCampaigns && (
                      <FormControlLabel
                        value="campaign"
                        control={<Radio size="small" />}
                        label="par campagne"
                        sx={{ mr: 1.25 }}
                      />
                    )}
                  </PermissionDecorator>
                </RadioGroup>
              </FormControl>
            ) : null}

            {renderForm()}
          </Box>
        </>
      ) : (
        <>
          <FormControl className="nav-form-group" sx={{ width: '100%' }}>
            <FormLabel component="legend">Plages temporelles</FormLabel>

            {!(
              (noCampaigns ? 0 : 1) +
                (noCalendar ? 0 : 1) +
                (noRelative ? 0 : 1) <=
              1
            ) ? (
              <RadioGroup
                row
                name="dynamicRender"
                value={selectedOption}
                onChange={(e) =>
                  setSelectedOption(e.currentTarget.value as Option)
                }
              >
                {!noCalendar && (
                  <FormControlLabel
                    value="calendar"
                    control={<Radio />}
                    label="calendaires"
                  />
                )}
                {!noRelative && (
                  <FormControlLabel
                    value="relative"
                    control={<Radio />}
                    label="relatifs"
                  />
                )}
                <PermissionDecorator item="campaigns_tab">
                  {!noCampaigns && (
                    <FormControlLabel
                      value="campaign"
                      control={<Radio />}
                      label="par campagne"
                    />
                  )}
                </PermissionDecorator>
              </RadioGroup>
            ) : null}
          </FormControl>

          {renderForm()}
        </>
      )}
    </Box>
  );
}

export default TimeRangeUnionForm;
